import React, { useCallback, useState, useEffect, useContext } from 'react';
import {
  LogBehaviourActions,
  TextCollectionFieldType,
  TextField,
} from '@scale/shared/types/params/textcollection';
import { TextFieldAnnotation } from '@scale/shared/types/response/textcollection';
import { ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';
import _ from 'lodash';
import { analytics } from 'react-scale/lib/analytics/segment-source-scalers';
import { useAttemptBehaviorLog } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/AttemptBehaviorLogContext';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';
import {
  Editor,
  EditorContainer,
  FooterContainer,
  PlainTextEditorContext,
  WordCounter,
} from '../../ThreadedInteraction/components/editor';

interface SimpleTextFieldProps {
  field: TextField;
  annotation: TextFieldAnnotation;
  setAnnotation: (annotation: TextFieldAnnotation) => void;
  disabled?: boolean;
  logBehaviourActions?: LogBehaviourActions;
  isTestQuestion?: boolean;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
}

interface TypingEvent {
  timestamp: number;
  lengthDiff: number;
}

const AUTOTYPER_DETECT_STEP = 25;
const AUTOTYPER_DETECT_LENGTH = 100;
const AUTOTYPER_DETECT_MAX_INTERVAL_MS = 2000;
const DISABLE_PASTING_ERROR_TEXT = `This field doesn't allow pasting`;

const SimpleTextField: React.FC<SimpleTextFieldProps> = props => {
  const [typingEvents, setTypingEvents] = useState<TypingEvent[]>([]);
  const {
    emitTypingEvent: chatTaskEmitTypingEvent,
    incMouseLeaveEvent: chatTaskIncMouseLeaveEvent,
  } = useAttemptBehaviorLog();

  const [errorText, setErrorText] = useState<string | undefined>();
  const [value, setValue] = useState<string | undefined>(props.annotation?.response?.[0]);
  const taskContext = useContext(ChatTaskContext);

  const disablePasting =
    taskContext.disablePastingAllTextCollectionFields ||
    (taskContext.disablePastingTextCollectionFieldIds ?? []).includes(props.field.field_id);
  const disableCopying = false;

  useEffect(() => {
    if (props.field.default_text && !props.annotation) {
      setValue(props.field.default_text);
    }
  }, [props.field.default_text, props.annotation]);

  // eslint-disable-next-line
  const debouncedDetectAutotyper = useCallback(
    _.debounce((newTypingEvents: TypingEvent[]) => {
      if (!newTypingEvents || newTypingEvents.length < AUTOTYPER_DETECT_LENGTH) {
        return;
      }
      const keystrokeIntervals: number[] = [];
      for (const i of [...Array(newTypingEvents.length - 1).keys()]) {
        keystrokeIntervals.push(newTypingEvents[i + 1].timestamp - newTypingEvents[i].timestamp);
      }
      const numKeystrokes = keystrokeIntervals.length;
      let autotyperDetected = false;
      for (let step = 0; step < numKeystrokes; step = step + AUTOTYPER_DETECT_STEP) {
        if (step + AUTOTYPER_DETECT_LENGTH > numKeystrokes) {
          break;
        }
        const subset = keystrokeIntervals.slice(step, step + AUTOTYPER_DETECT_LENGTH);

        const avgInterval = _.mean(subset) ?? 0.0;
        if (avgInterval > AUTOTYPER_DETECT_MAX_INTERVAL_MS) {
          continue;
        }
        if (avgInterval / 2 > (_.max(subset) ?? 0.0) - (_.min(subset) ?? 0.0)) {
          autotyperDetected = true;
          break;
        }
      }
      if (disablePasting && autotyperDetected) {
        setErrorText(DISABLE_PASTING_ERROR_TEXT);
        if (props.isTestQuestion) {
          analytics.track('Training - User tried to paste');
        }
      }
      if (
        autotyperDetected &&
        props.field.field_id &&
        props.logBehaviourActions?.handleAutotyperDetectedEvent
      ) {
        props.logBehaviourActions?.handleAutotyperDetectedEvent(props.field.field_id);
      }
    }, 1000),
    [props.field.field_id, props.logBehaviourActions],
  );

  // eslint-disable-next-line
  const debouncedUpdateBehaviourLog = useCallback(
    _.debounce((newTypingEvents: TypingEvent[]) => {
      if (!props.field.field_id || !newTypingEvents || !newTypingEvents.length) {
        return;
      }

      if (props.logBehaviourActions?.handleActiveTypingEvent) {
        props.logBehaviourActions?.handleActiveTypingEvent(
          props.field.field_id,
          (_.last(newTypingEvents)?.timestamp ?? 0) - (_.first(newTypingEvents)?.timestamp ?? 0),
        );
      }

      if (props.logBehaviourActions?.appendLengthDiffEvents) {
        props.logBehaviourActions?.appendLengthDiffEvents(
          props.field.field_id,
          newTypingEvents.map((e: TypingEvent) => e.lengthDiff),
        );
      }

      setTypingEvents([]);
    }, 800),
    [props.field.field_id, props.logBehaviourActions],
  );

  const handleChange = useCallback(
    (newValue: string, timestamp: number) => {
      const lengthDiff = newValue.length - (value?.length ?? 0);

      setValue(newValue);

      // Saving typing events to aggregate and log them later.
      const newTypingEvents = [
        ...typingEvents,
        {
          timestamp,
          lengthDiff,
        },
      ];
      setTypingEvents(newTypingEvents);
      debouncedUpdateBehaviourLog(newTypingEvents);
      debouncedDetectAutotyper(newTypingEvents);
      if (props.field.field_id && chatTaskEmitTypingEvent) {
        chatTaskEmitTypingEvent({
          fieldId: props.field.field_id,
          text: newValue,
          responseIndex: props.chatTaskBehaviorLogMetadata?.responseIndex,
          suffix: props.chatTaskBehaviorLogMetadata?.suffix,
        });
      }
    },
    [
      value,
      typingEvents,
      debouncedUpdateBehaviourLog,
      debouncedDetectAutotyper,
      props.field.field_id,
      chatTaskEmitTypingEvent,
      props.chatTaskBehaviorLogMetadata,
    ],
  );

  const onMouseLeave = () => {
    if (props.field.field_id && props.logBehaviourActions?.handleMouseLeaveEvent) {
      props.logBehaviourActions.handleMouseLeaveEvent(props.field.field_id);
    }
    if (props.field.field_id && chatTaskIncMouseLeaveEvent) {
      chatTaskIncMouseLeaveEvent({
        fieldId: props.field.field_id,
        responseIndex: props.chatTaskBehaviorLogMetadata?.responseIndex,
        suffix: props.chatTaskBehaviorLogMetadata?.suffix,
      });
    }
  };

  const handleBlur = useCallback(
    (value: any) => {
      props.setAnnotation({
        type: TextCollectionFieldType.Text,
        field_id: props.annotation.field_id,
        response: [value],
      });
    },
    [props],
  );

  return (
    <div className="flex flex-col gap-2">
      <FieldHeader
        title={props.field.title}
        description={props.field.description}
        hint={props.field.hint}
      />
      <PlainTextEditorContext initialContent={value}>
        <EditorContainer className="w-full">
          <Editor
            data-field-id={props.field.field_id}
            disabled={props.disabled}
            value={value}
            onContentChange={(value, timestamp) => handleChange(value, timestamp)}
            disablePaste={disablePasting}
            onDisabledPaste={() => setErrorText(DISABLE_PASTING_ERROR_TEXT)}
            disableCopy={disableCopying}
            onMouseLeave={onMouseLeave}
            onEditorBlur={({ content }) => handleBlur(content)}
          />
          <FooterContainer>
            <WordCounter />
          </FooterContainer>
        </EditorContainer>
        <div className="text-sm text-red-500">{errorText}</div>
      </PlainTextEditorContext>
    </div>
  );
};

export default SimpleTextField;
